import { Api, Codes } from 'api';
import _ from 'lodash';

/**
 * 判断 submitted_at 是否为有效提交时间。
 *
 * @param {*} value submitted_at 字段值。
 * @returns {boolean} 有效返回 true。
 */
export function hasValidSubmittedAt(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (_.isString(value)) {
    return value.trim() !== '';
  }

  return true;
}

/**
 * 规范化通用表单接口返回值。
 *
 * @param {Object|null|undefined} raw 接口返回的原始表单数据。
 * @param {string} formName 表单名。
 * @returns {{ form_name: string, submitted_at: string, value: Object, exists: boolean }} 规范化结果。
 */
export function normalizeStudentForm(raw, formName = '') {
  const exists = !!raw && (!_.isPlainObject(raw) || Object.keys(raw).length > 0);
  const source = exists && _.isPlainObject(raw) ? raw : {};
  const value = _.isPlainObject(source.value)
    ? { ...source.value }
    : { ...source };
  const resolvedFormName = source.form_name || formName;
  const submittedAt = source.submitted_at || value.submitted_at || '';

  delete value.form_name;
  value.submitted_at = submittedAt || '';

  return {
    form_name: resolvedFormName,
    submitted_at: submittedAt || '',
    value,
    exists
  };
}

/**
 * 判断通用表单是否已提交。
 *
 * @param {Object} form 规范化后的表单数据。
 * @returns {boolean} 已提交返回 true。
 */
export function isFormSubmitted(form) {
  if (!form) {
    return false;
  }

  const exists = form.exists === undefined
    ? Object.keys(form).length > 0
    : !!form.exists;

  return exists && hasValidSubmittedAt(form.submitted_at || form.value?.submitted_at);
}

function parseGetPayload(payload, legacyArray = false) {
  if (_.isString(payload) || _.isArray(payload)) {
    return {
      id: payload,
      array: legacyArray,
      force: false
    };
  }

  if (_.isPlainObject(payload)) {
    return {
      id: payload.id || payload.ids || payload.formName || payload.formNames || payload.form_name,
      array: !!payload.array,
      force: !!payload.force
    };
  }

  return {
    id: payload,
    array: legacyArray,
    force: false
  };
}

function toIdList(ids) {
  if (_.isArray(ids)) {
    return ids;
  }

  if (_.isString(ids) && ids) {
    return [ids];
  }

  return [];
}

function pickSubmittedAtFromResponse(res) {
  return res?.data?.submitted_at || res?.data?.value?.submitted_at || '';
}
/**
 * 表单相关的 Vuex 模块
 * 管理表单数据（forms）和查询状态（queries）
 */
export default {
  // state：存储表单数据和查询状态
  state: () => ({
    forms: {}, // 已获取的表单数据，key 为表单名
    queries: {} // 正在查询的表单，key 为表单名，value 为 Promise
  }),
  // mutations：同步修改 state 的方法
  mutations: {
    /**
     * 设置查询状态
     * @param state
     * @param payload { id, query }
     */
    setQuery: (state, payload) => {
      state.queries[payload.id] = payload.query;
    },
    /**
     * 清除某个表单的查询状态
     * @param state
     * @param id 表单名
     */
    clearQuery: (state, id) => {
      delete state.queries[id];
    },
    /**
     * 设置表单数据
     * @param state Vuex state对象
     * @param payload 包含表单id和表单数据的对象
     * @param payload.id 表单id,用作forms对象的key
     * @param payload.form 表单数据,将被存储到forms对象中
     * @description 将表单数据存储到state.forms中。首先复制当前forms对象,
     * 然后将新的表单数据以id为key存入,最后更新state中的forms对象。
     * 这样做可以保证数据的响应式更新。
     */
    setForm: (state, payload) => {
      let forms = {
        ...state.forms
      };
      forms[payload.id] = payload.form;
      state.forms = forms;
    },
    /**
     * 清除某个表单数据
     * @param state
     * @param id 表单名
     */
    clearForm: (state, id) => {
      delete state.forms[id];
    }
  },
  getters: {},
  // actions：异步操作，处理表单数据的获取和缓存
  actions: {
    /**
     * 获取表单数据，支持单个或多个 id
     * @param dispatch
     * @param id 表单名或表单名数组
     * @param array 是否返回数组格式
     * @returns {Promise}
     */
    getFormData: ({ dispatch }, payload, array = false) => {
      const { id, array: shouldReturnArray, force } = parseGetPayload(payload, array);
      // 内部方法：根据 array 参数决定返回对象还是数组
      const _array = res => {
        let ret = res;
        if (shouldReturnArray) {
          ret = Object.keys(res).map(key => res[key]);
        }
        return ret;
      };
      return new Promise(resolve => {
        if (_.isArray(id)) {
          // 多个 id，调用 getFormDataByIds
          dispatch('getFormDataByIds', { ids: id, force }).then(res => {
            resolve(_array(res));
          });
        } else if (_.isString(id)) {
          // 单个 id，调用 getFormDataById
          dispatch('getFormDataById', { id, force }).then(res => {
            resolve(_array(res));
          });
        }
      });
    },
    refreshFormData: ({ dispatch }, payload) => {
      const { id, array } = parseGetPayload(payload);

      return dispatch('getFormData', { id, array, force: true });
    },
    /**
     * 获取单个表单数据，优先从缓存读取
     * @param state
     * @param dispatch
     * @param id 表单名
     * @returns {Promise}
     */
    getFormDataById: ({ state, dispatch }, payload) => {
      const { id, force } = parseGetPayload(payload);
      return new Promise(resolve => {
        if (!force && state.forms[id]) {
          // 已有缓存，直接返回
          resolve(state.forms[id]);
        } else {
          // 无缓存，发起查询
          dispatch('queryForm', id).then(res => {
            let ret = {};
            if (res[id]) {
              ret[id] = res[id];
            }
            resolve(ret[id] || normalizeStudentForm(null, id));
          });
        }
      });
    },
    /**
     * 获取多个表单数据，优先从缓存读取
     * @param state
     * @param dispatch
     * @param ids 表单名数组
     * @returns {Promise}
     */
    getFormDataByIds: ({ state, dispatch }, payload) => {
      const { id: ids, force } = parseGetPayload(payload);
      return new Promise(resolve => {
        let queryIds = []; // 需要查询的表单 id
        let ret = {};
        ids.forEach(id => {
          if (!force && state.forms[id]) {
            ret[id] = state.forms[id];
          } else {
            queryIds.push(id);
          }
        });
        if (queryIds.length === 0) {
          // 全部有缓存，直接返回
          resolve(ret);
        } else {
          // 有部分未缓存，发起查询
          dispatch('queryForm', queryIds).then(res => {
            queryIds.forEach(id => {
              ret[id] = res[id] || normalizeStudentForm(null, id);
            });
            resolve(ret);
          });
        }
      });
    },
    /**
     * 查询表单，支持单个或多个 id，带有查询缓存，防止重复请求
     * @param commit
     * @param state
     * @param dispatch
     * @param ids 表单名或表单名数组
     * @returns {Promise|*}
     */
    queryForm: ({ commit, state, dispatch }, ids) => {
      const isArray = _.isArray(ids);
      if (isArray) {
        let pendingQueries = [];
        let newIds = [];
        ids.forEach(id => {
          if (state.queries[id]) {
            // 已有查询缓存
            if (!pendingQueries.includes(state.queries[id])) {
              pendingQueries.push(state.queries[id]);
            }
          } else {
            newIds.push(id);
          }
        });
        if (newIds.length) {
          // 有部分未查询，发起新查询
          let query = dispatch('queryFormApi', newIds);
          newIds.forEach(id => {
            commit('setQuery', {
              id,
              query
            });
          });
          pendingQueries.push(query);
        }

        return Promise.all(pendingQueries).then(results => Object.assign({}, ...results));
      } else if (_.isString(ids)) {
        if (state.queries[ids]) {
          // 已有查询缓存，直接返回
          return state.queries[ids];
        } else {
          // 发起新查询
          commit('setQuery', {
            id: ids,
            query: dispatch('queryFormApi', [ids])
          });
          return state.queries[ids];
        }
      } else {
        // 兜底，直接调用 API
        return dispatch('queryFormApi', ids);
      }
    },
    /**
     * 通过 API 查询表单数据，查询成功后写入缓存
     * @param commit
     * @param state
     * @param ids 表单名或表单名数组
     * @returns {Promise}
     */
    queryFormApi: ({ commit, state }, ids) => {
      return new Promise(resolve => {
        const queryIds = toIdList(ids);
        let ret = {
          ...state.forms
        };
        // 调用后端 API 获取表单数据
        Api.get
          .getStudentForm({
            form_name: queryIds.join(',')
          })
          .then(res => {
            if (res.status === Codes.SUCCESS) {
              const rawList = _.isArray(res.data)
                ? res.data
                : [res.data].filter(Boolean);
              const rawMap = {};

              rawList.forEach(item => {
                const formName = item?.form_name;
                if (formName) {
                  rawMap[formName] = item;
                }
              });

              queryIds.forEach(id => {
                const raw = rawMap[id] || (!_.isArray(res.data) && queryIds.length === 1 ? res.data : null);
                const form = normalizeStudentForm(raw, id);
                ret[id] = form;
                commit('setForm', {
                  id,
                  form
                });
              });
            }
            // 查询结束后清除查询缓存
            queryIds.forEach(id => {
              commit('clearQuery', id);
            });
            resolve(ret);
          });
      });
    },
    /**
     * 提交表单数据并更新缓存
     * @param commit 提交 mutation 的方法
     * @param state Vuex state
     * @param payload 包含表单名和表单数据的对象 { form_name, form }
     * @returns {Promise} 返回提交结果
     */
    setFormData: ({ commit, state }, payload) => {
      return new Promise(resolve => {
        const formName = payload.form_name;
        const formValue = payload.form !== undefined ? payload.form : (payload.value || {});
        const requestData = {
          form_name: formName,
          value: formValue
        };

        if (payload.draft !== undefined) {
          requestData.draft = payload.draft;
        }

        // 调用后端 API 提交表单数据
        Api.post
          .postStudentForm(requestData)
          .then(res => {
            if (res.status === Codes.SUCCESS) {
              const prev = state.forms[formName];
              const responseSubmittedAt = pickSubmittedAtFromResponse(res);
              const prevSubmittedAt = prev?.submitted_at || prev?.value?.submitted_at || '';
              const submittedAt = hasValidSubmittedAt(responseSubmittedAt)
                ? responseSubmittedAt
                : (payload.draft ? prevSubmittedAt : new Date().toISOString());
              const nextValue = prev
                ? {
                    ...prev.value,
                    ...formValue,
                    submitted_at: submittedAt
                  }
                : {
                    ...formValue,
                    submitted_at: submittedAt
                  };
              const form = normalizeStudentForm({
                form_name: formName,
                submitted_at: submittedAt,
                value: nextValue
              }, formName);
              form.exists = true;

              commit('setForm', {
                id: formName,
                form
              });
              res.form = form;
            }
            resolve(res);
          });
      });
    }
  }
};
